import React, { useCallback, useMemo, useRef, useState } from 'react';

import { FlatList, ListRenderItemInfo, Platform, View } from 'react-native';

import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { StackNavigationProp } from '@react-navigation/stack';
// eslint-disable-next-line import/no-extraneous-dependencies
import { debounce } from 'lodash';

import { RightArrowSettings, settingsIcons } from 'assets/images';

import gStyles from 'config/styles';

import { SCREEN } from 'router/screens';
import { RouteNavigationParams } from 'router/types';

import { deleteAllBookmarks } from 'store/bookmarks/actions';
import { selectLayoutConfig } from 'store/config/selectors';
import { setSpotlightVideoVisible } from 'store/player/actions';
import { selectSpotlightVideoVisible } from 'store/player/selectors';
import {
    MenuConfigurationItem,
    RBTemplates,
} from 'store/railsBuilder/reducer.types';
import { selectSettingsPageConfig } from 'store/railsBuilder/selectors';
import { selectLanguage } from 'store/user/selectors';

import { DidomiRef } from 'services/consents-management/DidomiSDK/utils/createDidomiRef';

import LANGUAGES_NAMES from 'utils/Language/languageNames';
import debugLog from 'utils/debugLog.ts';
import useHeaderConfig from 'utils/hooks/useHeaderConfig';
import { isWeb } from 'utils/responsiveness';

import { underlayingPlayerModalElementsDataSet } from 'screens/PlayerModal/hooks/useModalAccessibility.ts';

import DialogView from 'components/Dialog';

import SettingsRowItem from './components/RowItem';
import SettingsDialogDescription from './components/SettingsDialogDescription';
import WebHeader from './components/WebHeader';
import useChannelsLineups from './hooks/useChannelsLineups';
import {
    CLOSE_BUTTON,
    DELETE_BUTTON,
    SCREEN_SETTINGS_ALERT_AUDIO_SUBTITLES_BODY,
    SCREEN_SETTINGS_ALERT_AUDIO_SUBTITLES_OK_BUTTON,
    SCREEN_SETTINGS_ALERT_AUDIO_SUBTITLES_TITLE,
    SETTINGS_DELETE_HISTORY_NOTIFICATION_DESCRIPTION,
    SETTINGS_DELETE_HISTORY_NOTIFICATION_TITLE,
    SETTINGS_SECTION_TITLE,
} from './languagesConst';
import styles from './styles';

type Props = {
    navigation: StackNavigationProp<RouteNavigationParams, SCREEN.SETTINGS>;
};

function SettingsPage({ navigation }: Props) {
    const { t } = useTranslation();
    const dispatch = useDispatch();

    const settingsPageConfig = useSelector(selectSettingsPageConfig);
    const { primary, background } = useSelector(selectLayoutConfig);
    const currentLanguage = useSelector(selectLanguage);
    const spotlightPlayerVisible = useSelector(selectSpotlightVideoVisible);

    const [audioAndSubtitlesDialogVisible, setAudioAndSubtitlesDialogVisible] =
        useState(false);
    const [deleteHistoryDialogVisible, setDeleteHistoryDialogVisible] =
        useState(false);
    const { selectedChannelsLineup } = useChannelsLineups();

    const listStyle = useMemo(
        () => ({
            backgroundColor: background,
        }),
        [background],
    );

    const handlePressHelp = useCallback(() => {
        navigation.navigate(SCREEN.SETTINGS_HELP);

        if (Platform.OS !== 'web') {
            navigation.getParent()?.setParams({
                forceTabHide: true,
            });
        }
    }, [navigation]);

    const handlePressLogout = useCallback(() => {
        navigation.navigate(SCREEN.SETTINGS_LOGOUT);
    }, [navigation]);

    const handleCloseAudioAndSubtitlesDialog = useCallback(() => {
        setAudioAndSubtitlesDialogVisible(false);
    }, []);

    const handleCloseDeleteHistoryDialog = useCallback(() => {
        setDeleteHistoryDialogVisible(false);
    }, []);

    const handleOpenAudioAndSubtitles = useCallback(() => {
        navigation.navigate(SCREEN.AUDIO_AND_SUBTITLES);
        handleCloseAudioAndSubtitlesDialog();
    }, [navigation, handleCloseAudioAndSubtitlesDialog]);

    const handleDeleteHistory = useCallback(() => {
        dispatch(deleteAllBookmarks());
        handleCloseDeleteHistoryDialog();
    }, [handleCloseDeleteHistoryDialog, dispatch]);

    const renderAudioAndSubtitlesDescription = useCallback(
        () => (
            <SettingsDialogDescription
                description={t(SCREEN_SETTINGS_ALERT_AUDIO_SUBTITLES_BODY)}
            />
        ),
        [t],
    );

    const renderDeleteHistoryDescription = useCallback(
        () => (
            <SettingsDialogDescription
                description={t(
                    SETTINGS_DELETE_HISTORY_NOTIFICATION_DESCRIPTION,
                )}
            />
        ),
        [t],
    );

    const handleSettingsMenuItemPress = useCallback(
        ({ name }: MenuConfigurationItem) => {
            switch (name) {
                case RBTemplates.Parental:
                    navigation.navigate(SCREEN.PARENTAL_CONTROL_AND_PIN);
                    break;
                case RBTemplates.ChannelLineup:
                    navigation.navigate(SCREEN.CHANNELS_LINEUP);
                    break;
                case RBTemplates.Language:
                    navigation.navigate(SCREEN.LANGUAGE);
                    break;
                case RBTemplates.Quota:
                    navigation.navigate(SCREEN.SETTINGS_RECORDING_QUOTA);
                    break;
                case RBTemplates.InternetConnection:
                    navigation.navigate(SCREEN.SETTINGS_INTERNET_CONNECTION);
                    break;
                case RBTemplates.Subscriptions:
                    navigation.navigate(SCREEN.SETTINGS_SUBSCRIPTIONS);
                    break;
                case RBTemplates.EpgDisplay:
                    navigation.navigate(SCREEN.SETTINGS_EPG_DISPLAY);
                    break;
                case RBTemplates.AudioSubtitles:
                case RBTemplates.AudioSubtitlesKids:
                    setAudioAndSubtitlesDialogVisible(true);
                    break;
                case RBTemplates.DeleteHistory:
                    setDeleteHistoryDialogVisible(true);
                    break;
                case RBTemplates.Consents:
                    DidomiRef.current
                        ?.showPreferences()
                        .then((e) => {
                            debugLog('Didomi', 'showPreferences success', e);
                        })
                        .catch((error) => {
                            debugLog('Didomi', 'showPreferences error', error);
                        });
                    break;
                case RBTemplates.SpotlightVideo:
                    dispatch(setSpotlightVideoVisible(true));
                    break;
                case RBTemplates.About:
                case RBTemplates.AboutKids:
                    navigation.navigate(SCREEN.ABOUT);
                    break;
                case RBTemplates.Help:
                case RBTemplates.HelpKids:
                    handlePressHelp();
                    break;
                case RBTemplates.BackendInfo:
                case RBTemplates.BackendInfoKids:
                    navigation.navigate(SCREEN.SETTINGS_BACKEND_INFO);
                    break;
                case RBTemplates.TermsConditions:
                    navigation.navigate(SCREEN.SETTINGS_TERMS_CONDITIONS);
                    break;
                case RBTemplates.SignOut:
                    handlePressLogout();
                    break;
                default:
                    break;
            }
        },
        [handlePressHelp, handlePressLogout, navigation, dispatch],
    );

    const debouncedSettingsMenuItemPress = useRef(
        debounce(handleSettingsMenuItemPress, 500, {
            leading: true,
            trailing: false,
        }),
    );

    const renderItem = useCallback(
        ({
            item: { POEditorKey, name, icon },
            item,
            index,
        }: ListRenderItemInfo<MenuConfigurationItem>) => {
            const Icon = settingsIcons[icon as keyof typeof settingsIcons];
            const position = index + 1;
            const total = (settingsPageConfig || []).length;

            switch (name) {
                case RBTemplates.Language:
                    return (
                        <SettingsRowItem
                            testID={item.testID}
                            item={item}
                            title={t(POEditorKey)}
                            subtitle={
                                // @ts-ignore
                                t(LANGUAGES_NAMES[currentLanguage].key) ||
                                LANGUAGES_NAMES[currentLanguage].englishName
                            }
                            Icon={Icon}
                            RightIcon={RightArrowSettings}
                            onPress={debouncedSettingsMenuItemPress.current}
                            position={position}
                            total={total}
                        />
                    );
                case RBTemplates.ChannelLineup:
                    return (
                        <SettingsRowItem
                            testID={item.testID}
                            item={item}
                            title={t(POEditorKey)}
                            subtitle={
                                selectedChannelsLineup?.title
                                    ? t(selectedChannelsLineup.title)
                                    : ''
                            }
                            Icon={Icon}
                            RightIcon={RightArrowSettings}
                            onPress={debouncedSettingsMenuItemPress.current}
                            position={position}
                            total={total}
                        />
                    );
                case RBTemplates.DeleteHistory:
                    return (
                        <SettingsRowItem
                            testID={item.testID}
                            item={item}
                            title={t(POEditorKey)}
                            Icon={Icon}
                            onPress={debouncedSettingsMenuItemPress.current}
                            position={position}
                            total={total}
                        />
                    );
                default:
                    return (
                        <SettingsRowItem
                            testID={item.testID}
                            item={item}
                            title={t(POEditorKey)}
                            Icon={Icon}
                            RightIcon={RightArrowSettings}
                            onPress={debouncedSettingsMenuItemPress.current}
                            position={position}
                            total={total}
                        />
                    );
            }
        },
        [t, currentLanguage, selectedChannelsLineup, settingsPageConfig],
    );

    useHeaderConfig({
        isRoot: isWeb,
        disableFocusOnMount: true,
    });

    return (
        <>
            {isWeb && (
                <WebHeader
                    title={t(SETTINGS_SECTION_TITLE)}
                    disableFocusOnMount
                />
            )}
            <View
                style={[
                    gStyles.settingsContainer,
                    { backgroundColor: background },
                ]}
                importantForAccessibility={
                    spotlightPlayerVisible ? 'no-hide-descendants' : 'auto'
                }
                accessibilityElementsHidden={spotlightPlayerVisible}
                dataSet={underlayingPlayerModalElementsDataSet}
            >
                <FlatList
                    style={[gStyles.settingsContentContainer, listStyle]}
                    nestedScrollEnabled
                    data={settingsPageConfig}
                    keyExtractor={keyExtractor}
                    renderItem={renderItem}
                    showsHorizontalScrollIndicator={false}
                />
                <DialogView
                    animationType="fade"
                    style={styles.dialog}
                    visible={audioAndSubtitlesDialogVisible}
                    title={SCREEN_SETTINGS_ALERT_AUDIO_SUBTITLES_TITLE}
                    DescriptionComponent={renderAudioAndSubtitlesDescription}
                    actionButtonTitle={
                        SCREEN_SETTINGS_ALERT_AUDIO_SUBTITLES_OK_BUTTON
                    }
                    actionButtonAccessibilityLabel={t(
                        SCREEN_SETTINGS_ALERT_AUDIO_SUBTITLES_OK_BUTTON,
                    )}
                    actionButtonOnPress={handleOpenAudioAndSubtitles}
                    actionButtonFillColor={primary}
                    actionButtonStyle={styles.button}
                    actionButtonTextStyle={styles.backgroundButtonText}
                    cancelButtonStyle={styles.button}
                    cancelButtonOnPress={handleCloseAudioAndSubtitlesDialog}
                />
                <DialogView
                    animationType="fade"
                    style={styles.dialog}
                    visible={deleteHistoryDialogVisible}
                    title={SETTINGS_DELETE_HISTORY_NOTIFICATION_TITLE}
                    DescriptionComponent={renderDeleteHistoryDescription}
                    actionButtonTitle={DELETE_BUTTON}
                    actionButtonAccessibilityLabel={t(DELETE_BUTTON)}
                    actionButtonOnPress={handleDeleteHistory}
                    actionButtonStyle={styles.button}
                    actionButtonBordered
                    cancelButtonTitle={CLOSE_BUTTON}
                    cancelButtonAccessibilityLabel={t(CLOSE_BUTTON)}
                    cancelButtonFillColor={primary}
                    cancelButtonTextStyle={styles.backgroundButtonText}
                    cancelButtonOnPress={handleCloseDeleteHistoryDialog}
                />
            </View>
        </>
    );
}

export default SettingsPage;

function keyExtractor(item: MenuConfigurationItem) {
    return item.id;
}
